import {
  createAiContentPlugin,
  createBoardPlugin,
  createCalloutTipPlugin,
  createCodeBlockPlugin,
  createCodeHighlightBlockPlugin,
  createColPlugin,
  createCommentPlugin,
  createDeserializeMdPlugin,
  createEmbedPlugin,
  createFilePlugin,
  createIndentPlugin,
  createLegalPhPlugin,
  createMicroTaskPlugin,
  createMultiColPlugin,
  createTablePlusPlugin,
  // emojiPlugin,
  useSlatePluginsStore,
} from '@sketchnote/slate-plugins';
import { createPlugins, PlateProvider } from '@udecode/plate';
import {
  createAlignPlugin,
  createAutoformatPlugin,
  createBlockquotePlugin,
  createBoldPlugin,
  createCodePlugin,
  createComboboxPlugin,
  createDeserializeDocxPlugin,
  // createEmojiPlugin,
  createExitBreakPlugin,
  createFontBackgroundColorPlugin,
  createFontColorPlugin,
  createHeadingPlugin,
  createHighlightPlugin,
  createHorizontalRulePlugin,
  createImagePlugin,
  createItalicPlugin,
  createKbdPlugin,
  createLinkPlugin,
  createListPlugin,
  createMentionPlugin,
  createNodeIdPlugin,
  createParagraphPlugin,
  createPluginFactory,
  createResetNodePlugin,
  createSelectOnBackspacePlugin,
  createSoftBreakPlugin,
  createStrikethroughPlugin,
  createSubscriptPlugin,
  createSuperscriptPlugin,
  createTablePlugin,
  createTodoListPlugin,
  createUnderlinePlugin,
  PlatePlugin,
} from '@udecode/plate';
import { useMemo } from 'react';
import { Editor, Node, Scrubber } from 'slate';
import { ReactEditor } from 'slate-react';
import { API } from 'src/api';
import { BlockEditorProps } from 'src/components/BlockEditor';
import { CONFIG } from 'src/components/BlockEditor/configs/configs';
import { components } from 'src/components/BlockEditor/configs/plugin-components';
import { ELEMENT_CHANGE_BLOCK } from 'src/components/BlockEditor/ui/BlockCombobox';
import { v4 as uuid } from 'uuid';

const handleUploadImage = async (e) => {
  const data = new FormData();
  data.append('image', e);
  const response = await API.post('/creation/file', data, {
    headers: { ['Content-Type']: 'multipart/form-data' },
  });
  return response.data?.data?.message;
};

const sessionIdForMentions = uuid();

export const EditorProvider = ({
  id,
  children,
  editor,
  mode,
}: {
  id: string;
  children: React.ReactNode;
  editor?: ReactEditor;
  mode?: 'document' | 'lms' | 'chat';
}) => {
  const { removeTask } = useSlatePluginsStore();

  const handleOnMention: BlockEditorProps['onTag'] = async ({ blockId, tag, text }) => {
    await API.post('creation/tag', {
      memberId: [tag],
      creationId: id,
      text,
      blockId,
    });
  };

  const pluginsMemo: PlatePlugin[] = useMemo(() => {
    const pluginsT: PlatePlugin[] = createPlugins(
      [
        createParagraphPlugin(),
        createBlockquotePlugin(),
        createTodoListPlugin(),
        createHeadingPlugin(),
        createImagePlugin(),
        createHorizontalRulePlugin(),
        createLinkPlugin(),
        createListPlugin(),
        createTablePlugin(),
        createAlignPlugin(CONFIG.align),
        createBoldPlugin(),
        createCodePlugin(),
        createItalicPlugin(),
        createHighlightPlugin(),
        createUnderlinePlugin(),
        createStrikethroughPlugin(),
        createSubscriptPlugin(),
        createSuperscriptPlugin(),
        createFontColorPlugin(),
        createFontBackgroundColorPlugin(),
        createComboboxPlugin(),
        // createEmojiPlugin(emojiPlugin as any),
        createKbdPlugin(),
        createIndentPlugin(CONFIG.indent),
        createAutoformatPlugin(CONFIG.autoformat),
        createResetNodePlugin(CONFIG.resetBlockType),
        createSoftBreakPlugin(mode !== 'chat' ? CONFIG.softBreak : CONFIG.softBreakChat),
        createSelectOnBackspacePlugin(CONFIG.selectOnBackspace),
        createTodoListPlugin(),
        createEmbedPlugin(),
        createCodeBlockPlugin(),
        createCodeHighlightBlockPlugin(),
        createNodeIdPlugin({ options: { idCreator: () => uuid() } }),
        createCalloutTipPlugin(),
        createTablePlusPlugin(),
        createBoardPlugin(),
        createAiContentPlugin(),
        createLegalPhPlugin(),
        createMultiColPlugin(),
        createColPlugin(),
        createCommentPlugin(),
        createFilePlugin(),
        createImagePlugin({
          options: { uploadImage: handleUploadImage },
        }),
        createMicroTaskPlugin({ options: { onRemoveMicroTask: removeTask } }),
        createIndentPlugin({ options: { indentMax: 3 } }),
        createPluginFactory({ key: 'text-color', isElement: true, isInline: true })(),
        createExitBreakPlugin(mode !== 'chat' ? CONFIG.exitBreak : CONFIG.exitBreakChat),
        createMentionPlugin({
          options: {
            trigger: '@',
            insertSpaceAfterMention: true,
            inputCreation: { key: 'sessionId', value: sessionIdForMentions },
            createMentionNode: (item: any) => {
              if (editor?.selection) {
                const [node]: any = Editor.node(editor, [editor.selection.anchor.path[0]]);
                let text = Node.string(node);
                text += `@${item.text}`;
                if (mode !== 'chat') {
                  handleOnMention({ blockId: node.id, text, tag: item.key });
                }
              }
              return { data: item.data, value: '' };
            },
          },
        }),
        createMentionPlugin({
          key: ELEMENT_CHANGE_BLOCK,
          component: () => <></>,
          options: {
            trigger: '/',
            insertSpaceAfterMention: true,
            inputCreation: { key: 'sessionId', value: sessionIdForMentions },
          },
        }),
        createDeserializeMdPlugin(),
        createDeserializeDocxPlugin(),
      ],
      { components }
    );

    return pluginsT;
  }, [editor]);

  // Scrubber.setScrubber((k, v) => {
  //   return 'data-scrubbed';

  //  });

  return (
    <PlateProvider plugins={pluginsMemo} id={id}>
      {children}
    </PlateProvider>
  );
};
